import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { DECISION_TEXT } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export type StatusKind = 'posting' | 'sentiment' | 'plan' | 'generic';
export type StatusTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';
export type StatusBadgeShape = 'pill' | 'square';
/** `tint` (default): filled, no border — the original single shape. `outline`:
 * bordered at the same tone, for a state that must read as present but quieter
 * than the filled badge next to it. */
export type StatusBadgeFill = 'tint' | 'outline' | 'dot';

// `--destructive` is the ONE status token that gets DARKER in dark mode — 84%
// lightness in light (now retuned to red-700, see index.css), 30.6% in dark,
// while `--warning` (50%) and `--info` (60%) stay bright. As a BACKGROUND at
// /10 that is fine; as TEXT on a near-black card it was a ~2:1 contrast
// failure.
//
// Latent until 2026-08-21: every `tone="destructive"` in the repo was a
// style-guide showcase, so no user had seen it. `QualityChip` had
// independently worked around the same token with its own
// `dark:text-project-red`, and became the first production consumer of this
// tone when its trigger adopted these variants — which is how the gap
// surfaced. Both ad hoc `dark:text-project-red` borrows are retired now that
// `text-destructive` itself resolves through `--destructive-text` in dark
// (index.css), the single shared fix the comment above originally asked for.
//
// `fill: 'outline'` consolidates a second, previously hand-rolled shape:
// three inline pills in `AdminBrain.tsx` (`border-warning/30 bg-warning/10
// text-warning`, `border-success/30 bg-success/10 text-success`, and an
// unconditional warning-only "Estimated ledger" chip) plus
// `BriefConceptAdUnits.tsx`'s `COMPLETENESS_CHIP` (`border-border/60
// text-muted-foreground`, tone-less — i.e. `tone="neutral" fill="outline"`).
// Its own comment already named it "the outlined counterpart at identical
// geometry" to this badge; it now is one, instead of a sibling constant.
export const statusBadgeVariants = cva(
  // `whitespace-nowrap` — a status pill is a compact single-line label by
  // construction; no caller has ever wanted one to wrap. Without it, any
  // caller whose label runs long in a narrow column (e.g. a fixed-width
  // table cell) silently wraps to two lines instead of staying a pill — the
  // defect this repo already had at least twice independently before this
  // was made a base rule instead of a per-call-site fix (2026-09-21).
  'inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 text-detail font-medium',
  {
    variants: {
      tone: {
        neutral: '',
        success: '',
        warning: '',
        destructive: '',
        info: '',
      },
      fill: {
        tint: '',
        outline: 'border bg-transparent',
        dot: 'gap-1.5 px-0 py-0 font-normal',
      },
      shape: {
        pill: 'rounded-full',
        square: 'rounded-sm',
      },
    },
    compoundVariants: [
      { tone: 'neutral', fill: 'tint', class: 'bg-muted text-muted-foreground' },
      { tone: 'neutral', fill: 'outline', class: 'border-border/60 text-muted-foreground' },
      { tone: 'success', fill: 'tint', class: 'bg-success/10 text-success' },
      { tone: 'success', fill: 'outline', class: 'border-success/30 bg-success/10 text-success' },
      // `text-warning-ink`, not `text-warning`: measured 4.27:1 at 11px on
      // this badge's own composited ground, under the 4.5:1 AA floor. The ink
      // token is the same hue four points darker in light and IDENTICAL in
      // dark, which already passed. See its declaration in index.css.
      { tone: 'warning', fill: 'tint', class: 'bg-warning/10 text-warning-ink' },
      {
        tone: 'warning',
        fill: 'outline',
        class: 'border-warning/30 bg-warning/10 text-warning-ink',
      },
      {
        tone: 'destructive',
        fill: 'tint',
        class: 'bg-destructive/10 text-destructive',
      },
      {
        tone: 'destructive',
        fill: 'outline',
        class: 'border-destructive/30 bg-destructive/10 text-destructive',
      },
      { tone: 'info', fill: 'tint', class: 'bg-info/10 text-info-ink' },
      { tone: 'info', fill: 'outline', class: 'border-info/30 bg-info/10 text-info-ink' },
      { tone: 'neutral', fill: 'dot', class: `${DECISION_TEXT} text-muted-foreground` },
      { tone: 'success', fill: 'dot', class: `${DECISION_TEXT} text-success` },
      { tone: 'warning', fill: 'dot', class: `${DECISION_TEXT} text-warning-ink` },
      { tone: 'destructive', fill: 'dot', class: `${DECISION_TEXT} text-destructive` },
      { tone: 'info', fill: 'dot', class: `${DECISION_TEXT} text-info-ink` },
    ],
    defaultVariants: {
      tone: 'neutral',
      fill: 'tint',
      shape: 'pill',
    },
  },
);

const KIND_DEFAULT_TONE: Record<StatusKind, StatusTone> = {
  posting: 'neutral',
  sentiment: 'neutral',
  plan: 'info',
  generic: 'neutral',
};

export interface StatusBadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof statusBadgeVariants> {
  kind?: StatusKind;
  tone?: StatusTone;
  fill?: StatusBadgeFill;
  /** Square is reserved for dense data-grid state cells. */
  shape?: StatusBadgeShape;
  icon?: React.ReactNode;
  hollow?: boolean;
  children: React.ReactNode;
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  (
    {
      className,
      kind = 'generic',
      tone,
      fill = 'tint',
      shape = 'pill',
      icon,
      hollow = false,
      children,
      ...props
    },
    ref,
  ) => {
    const resolvedTone = tone ?? KIND_DEFAULT_TONE[kind];
    return (
      <span
        ref={ref}
        data-kind={kind}
        data-tone={resolvedTone}
        data-fill={fill}
        data-shape={shape}
        className={cn(statusBadgeVariants({ tone: resolvedTone, fill, shape }), className)}
        {...props}
      >
        {fill === 'dot' ? (
          <span
            aria-hidden
            data-dot={hollow ? 'hollow' : 'filled'}
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              hollow ? 'border border-current' : 'bg-current',
            )}
          />
        ) : icon ? (
          <span className="inline-flex shrink-0 items-center">{icon}</span>
        ) : null}
        <span className={fill === 'dot' ? undefined : 'leading-none'}>{children}</span>
      </span>
    );
  },
);
StatusBadge.displayName = 'StatusBadge';
